import { ApolloQueryResult } from '@apollo/client';
import { useDisclosure } from '@chakra-ui/react';
import {
  useGetBoaByShareLinkTokenQuery,
  useGetBuyerOfferAnalysisQuery,
} from '@client/graphql/__generated__/main-operations';
import {
  BuyerOfferAnalysisFragment,
  CompetitivenessAdjustmentInput,
  Exact,
  ListingComparableSetForBuyerOfferAnalysisInput,
  ListingComparableSetForBuyerOfferAnalysisQuery,
  ListingComparableSetFragment,
  ListingFragment,
  OfferRecsFragment,
  OfferScore,
  OfferScoreNotCalculated,
} from '@client/graphql/__generated__/types';
import { createContext, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { ComparableState } from '../../../pages/ListingSetup/Comparables/ComparablesPage';
import { getHeaderHeight } from '../constants';
import { useBuyerOfferAnalysisComps } from './hooks/useBuyerOfferAnalysisComps';
import { useUpdateBuyerOfferAnalysis } from './hooks/useUpdateBuyerOfferAnalysis';
import { AgentContingencyMap, AgentKeyTermMap } from './utils';

interface BuyerOfferAnalysisContextType {
  buyerOfferAnalysis?: BuyerOfferAnalysisFragment;
  listing?: ListingFragment;
  comparableSet?: ListingComparableSetFragment;
  comparableSetLoading: boolean;
  buyerOfferAnalysisLoading: boolean;
  offerRecsLoading: boolean;
  isEditCompsModalOpen: boolean;
  isChatOpen: boolean;
  setIsChatOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onEditCompsModalOpen: (comp?: ComparableState) => void;
  onEditCompsModalClose: () => void;
  comps: ComparableState[];
  setComps: React.Dispatch<React.SetStateAction<ComparableState[]>>;
  offerRecs?: OfferRecsFragment;
  refetchComparableSet: (
    variables?:
      | Partial<
          Exact<{ input: ListingComparableSetForBuyerOfferAnalysisInput }>
        >
      | undefined
  ) => Promise<
    ApolloQueryResult<ListingComparableSetForBuyerOfferAnalysisQuery>
  >;
  initialSelectedComp?: ComparableState;
  setInitialSelectedComp: React.Dispatch<
    React.SetStateAction<ComparableState | undefined>
  >;
  isCompsUpdated: boolean;
  setIsCompsUpdated: React.Dispatch<React.SetStateAction<boolean>>;
  isChangesSaved: boolean;
  title: string | undefined;
  setTitle: React.Dispatch<React.SetStateAction<string | undefined>>;
  updateTitle: (newTitle?: string) => void;
  updateHideOfferScore: (newHideOfferScore: boolean) => void;
  isUpdatingBuyerOfferAnalysis: boolean;
  agentNotes: string | undefined;
  setAgentNotes: React.Dispatch<React.SetStateAction<string | undefined>>;
  updateAgentNotes: (newNotes?: string) => void;
  agentContingencies: AgentContingencyMap;
  setAgentContingencies: React.Dispatch<
    React.SetStateAction<AgentContingencyMap>
  >;
  agentKeyTerms: AgentKeyTermMap;
  setAgentKeyTerms: React.Dispatch<React.SetStateAction<AgentKeyTermMap>>;
  updateCompetitiveness: (
    competitivenessAdjustment: CompetitivenessAdjustmentInput
  ) => Promise<void>;
  isEditingAgentNotes: boolean;
  setIsEditingAgentNotes: React.Dispatch<React.SetStateAction<boolean>>;
  offerScore: OfferScore | OfferScoreNotCalculated;
  isCalculatingOfferScore: boolean;
  isShareWithBuyerModalOpen: boolean;
  onShareWithBuyerModalOpen: () => void;
  onShareWithBuyerModalClose: () => void;
  isViewOnly: boolean;
  isViewCompsModalOpen: boolean;
  onViewCompsModalOpen: () => void;
  onViewCompsModalClose: () => void;
  isEditPartyModalOpen: boolean;
  onEditPartyModalOpen: () => void;
  onEditPartyModalClose: () => void;
  shouldOpenShareModal: boolean;
  setShouldOpenShareModal: React.Dispatch<React.SetStateAction<boolean>>;
  isAutofillingKeyTerms: boolean;
  // style
  isMobile?: boolean;
  headerHeight: React.CSSProperties['height'];
}

export const BuyerOfferAnalysisContext =
  createContext<BuyerOfferAnalysisContextType>(
    {} as BuyerOfferAnalysisContextType
  );

export const BuyerOfferAnalysisProvider = ({
  children,
  isViewOnly = false,
}: {
  children: React.ReactNode;
  isViewOnly?: boolean;
}) => {
  const { offerScorecardId, token } = useParams();
  const [isChatOpen, setIsChatOpen] = useState(window.innerWidth >= 1280);
  const [isEditingAgentNotes, setIsEditingAgentNotes] = useState(false);
  // triggers opening share modal after editing party if share button is clicked and boa does not have a party
  const [shouldOpenShareModal, setShouldOpenShareModal] = useState(false);
  const {
    isOpen: isShareWithBuyerModalOpen,
    onOpen: onShareWithBuyerModalOpen,
    onClose: onShareWithBuyerModalClose,
  } = useDisclosure();
  const {
    isOpen: isViewCompsModalOpen,
    onOpen: onViewCompsModalOpen,
    onClose: onViewCompsModalClose,
  } = useDisclosure();
  const {
    isOpen: isEditPartyModalOpen,
    onOpen: onEditPartyModalOpen,
    onClose: onEditPartyModalClose,
  } = useDisclosure();

  const { data: buyerOfferAnalysisData, loading: buyerOfferAnalysisLoading } =
    useGetBuyerOfferAnalysisQuery({
      variables: {
        uuid: offerScorecardId ?? '',
      },
      skip: !offerScorecardId || !!token,
      context: {
        queryDeduplication: false,
      },
    });

  const { data: tokenBOAData, loading: tokenBOADataLoading } =
    useGetBoaByShareLinkTokenQuery({
      variables: {
        input: {
          token: token || '',
          buyerOfferAnalysisId: offerScorecardId || '',
        },
      },
      skip: !token || !offerScorecardId,
    });
  const buyerOfferAnalysis = useMemo(
    () =>
      token
        ? tokenBOAData?.getBOAByShareLinkToken
        : buyerOfferAnalysisData?.getBuyerOfferAnalysis,
    [buyerOfferAnalysisData, tokenBOAData, token]
  );

  const listing = useMemo(
    () => buyerOfferAnalysis?.listing,
    [buyerOfferAnalysis]
  );

  // Contains all the logic for updating Indigo and Agent Offer Recs on the buyer offer analysis, competitiveness
  const {
    title,
    setTitle,
    updateTitle,
    updateHideOfferScore,
    agentNotes,
    setAgentNotes,
    updateAgentNotes,
    updateCompetitiveness,
    updateIndigoOfferRecs,
    isChangesSaved,
    offerRecsLoading,
    agentContingencies,
    setAgentContingencies,
    agentKeyTerms,
    setAgentKeyTerms,
    isCalculatingOfferScore,
    isUpdatingBuyerOfferAnalysis,
    isAutofillingKeyTerms,
  } = useUpdateBuyerOfferAnalysis({ buyerOfferAnalysis, listing, isViewOnly });

  // Contains all the update logic for the edit comps table and map
  const {
    comparableSetLoading,
    refetchComparableSet,
    initialSelectedComp,
    setInitialSelectedComp,
    isCompsUpdated,
    setIsCompsUpdated,
    comparableSet,
    onEditCompsModalOpen,
    onEditCompsModalClose,
    isEditCompsModalOpen,
    comps,
    setComps,
  } = useBuyerOfferAnalysisComps({
    buyerOfferAnalysis,
    updateIndigoOfferRecs,
    isViewOnly,
    token,
  });

  const isMobile = useIsMobile();

  const headerHeight = useMemo(() => {
    return getHeaderHeight({ isMobile: !!isMobile, isViewOnly });
  }, [isMobile, isViewOnly]);

  return (
    <BuyerOfferAnalysisContext.Provider
      value={{
        offerRecs: buyerOfferAnalysis?.indigoOfferRecs,
        buyerOfferAnalysis,
        listing,
        buyerOfferAnalysisLoading:
          tokenBOADataLoading || buyerOfferAnalysisLoading,
        comparableSetLoading,
        offerRecsLoading,
        comparableSet,
        isEditCompsModalOpen,
        onEditCompsModalOpen,
        onEditCompsModalClose,
        comps,
        setComps,
        refetchComparableSet,
        initialSelectedComp,
        setInitialSelectedComp,
        isCompsUpdated,
        setIsCompsUpdated,
        isChangesSaved,
        title,
        setTitle,
        updateTitle,
        updateHideOfferScore,
        isUpdatingBuyerOfferAnalysis,
        agentNotes,
        setAgentNotes,
        updateAgentNotes,
        agentContingencies,
        setAgentContingencies,
        agentKeyTerms,
        setAgentKeyTerms,
        isEditingAgentNotes,
        setIsEditingAgentNotes,
        updateCompetitiveness,
        offerScore: buyerOfferAnalysis?.offerScore ?? {
          reason: 'Not calculated yet',
        },
        isCalculatingOfferScore,
        isShareWithBuyerModalOpen,
        onShareWithBuyerModalOpen,
        onShareWithBuyerModalClose,
        isViewOnly,
        isChatOpen,
        setIsChatOpen,
        isViewCompsModalOpen,
        onViewCompsModalOpen,
        onViewCompsModalClose,
        headerHeight,
        isMobile,
        isEditPartyModalOpen,
        onEditPartyModalOpen,
        onEditPartyModalClose,
        shouldOpenShareModal,
        setShouldOpenShareModal,
        isAutofillingKeyTerms,
      }}
    >
      {children}
    </BuyerOfferAnalysisContext.Provider>
  );
};

export const useBuyerOfferAnalysisContext = () => {
  return useContext(BuyerOfferAnalysisContext);
};
